import { useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  HOME_AWARD_ANNOUNCEMENT_EXPIRES_AT,
  homeAwardAnnouncement,
} from '../../../content/homeAwardAnnouncement';
import { AWARD_POSTER_BACKGROUND, homeAwardPoster } from '../../../content/homeAwardPoster';
import AwardPoster from '../../../components/feature/AwardPoster';
import i18n from '../../../i18n';
import { normalizeSiteLanguage } from '../../../utils/currentSiteLanguage';
import { getHtmlLangFromRouteLanguage, getLanguageFromPath, type RouteLanguageCode } from '../../../utils/languageRouting';
import './home-award-announcement.css';

function announcementIsActive() {
  return Date.now() < new Date(HOME_AWARD_ANNOUNCEMENT_EXPIRES_AT).getTime();
}

function getLanguageFromCurrentSite(): RouteLanguageCode {
  const routeLanguage =
    typeof window !== 'undefined'
      ? getLanguageFromPath(window.location.pathname, __BASE_PATH__)
      : null;
  return routeLanguage || normalizeSiteLanguage(i18n.resolvedLanguage || i18n.language);
}

export default function HomeAwardAnnouncement() {
  const [siteLanguage, setSiteLanguage] =
    useState<RouteLanguageCode>(getLanguageFromCurrentSite);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isActive = announcementIsActive();
  const content = homeAwardAnnouncement[siteLanguage];
  const poster = homeAwardPoster[siteLanguage];

  function handleClose() {
    setOpen(false);
  }

  useEffect(() => {
    if (!isActive) return undefined;

    const handleSiteLanguageChanged = () => {
      setSiteLanguage(getLanguageFromCurrentSite());
    };

    i18n.on('languageChanged', handleSiteLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleSiteLanguageChanged);
    };
  }, [isActive]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousActiveElement = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previousActiveElement?.focus();
    };
  }, [open]);

  if (!isActive) return null;

  return (
    <>
      <section className="bg-[#050914] py-4 sm:py-5" dir="ltr">
        <div className="container mx-auto px-4 md:px-6">
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={`${poster.openLabel}: 2026 ${poster.title}`}
            aria-haspopup="dialog"
            className="group block w-full overflow-hidden rounded-2xl border border-amber-300/25 bg-black shadow-[0_18px_50px_rgba(0,0,0,0.35)] focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-gray-950"
          >
            <div className="transition duration-500 group-hover:brightness-110">
              <AwardPoster language={siteLanguage} />
            </div>
          </button>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 px-3 py-4 backdrop-blur-sm sm:px-6"
          onClick={handleClose}
          role="presentation"
        >
          <div
            ref={dialogRef}
            className="home-award-dialog relative max-h-[94vh] w-full max-w-5xl overflow-hidden rounded-3xl border border-amber-300/30 bg-white shadow-2xl animate-[fadeIn_0.25s_ease-out]"
            onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-award-announcement-title"
            lang={content.lang}
            dir={siteLanguage === 'ug' ? 'rtl' : 'ltr'}
          >
            <button
              ref={closeButtonRef}
              type="button"
              aria-label={poster.closeLabel}
              lang={getHtmlLangFromRouteLanguage(siteLanguage)}
              onClick={handleClose}
              className="absolute end-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white shadow-lg backdrop-blur transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-amber-300"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>

            <div className="max-h-[94vh] overflow-y-auto overscroll-contain">
              <article className="home-award-article" lang={content.lang}>
                <div className="grid bg-gray-950 lg:grid-cols-[0.92fr_1.08fr]">
                  <div
                    className="relative min-h-56 overflow-hidden bg-gray-900 bg-no-repeat sm:min-h-72 lg:min-h-[31rem]"
                    style={{
                      backgroundImage: `url('${AWARD_POSTER_BACKGROUND}')`,
                      backgroundPosition: 'right center',
                      backgroundSize: '250% auto',
                    }}
                    role="img"
                    aria-label={`2026 ${poster.title}`}
                    lang={getHtmlLangFromRouteLanguage(siteLanguage)}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/10 lg:bg-gradient-to-r lg:from-transparent lg:to-black/35" />
                    <div className="absolute bottom-5 left-5 right-5">
                      <span
                        className="award-poster-caption inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-black/70 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.13em] text-amber-200 backdrop-blur"
                        lang={getHtmlLangFromRouteLanguage(siteLanguage)}
                        dir={siteLanguage === 'ug' ? 'rtl' : 'ltr'}
                      >
                        <i className="ri-medal-line" aria-hidden="true" />
                        {poster.title}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center px-6 py-8 text-white sm:px-9 sm:py-10 lg:px-12">
                    <div className="mb-5 pe-10">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                        {content.eyebrow} · {content.location}
                      </div>
                    </div>

                    <h2
                      id="home-award-announcement-title"
                      className="text-3xl font-bold leading-tight text-white sm:text-4xl"
                    >
                      {content.title}
                    </h2>

                    <p className="mt-4 text-sm font-medium text-gray-400">{content.date}</p>
                    <p className="mt-6 text-base leading-7 text-gray-200">{content.summary}</p>
                  </div>
                </div>

                <div
                  id="home-award-full-release"
                  className="bg-white px-6 py-9 text-gray-700 sm:px-10 sm:py-12 lg:px-16"
                >
                  <div className="mx-auto max-w-3xl space-y-10">
                    <div className="space-y-4 text-[15px] leading-8 sm:text-base">
                      {content.introduction.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>

                    {content.sections.map((section) => (
                      <section key={section.heading}>
                        <h3 className="border-s-4 border-amber-500 ps-4 text-xl font-bold leading-snug text-gray-950 sm:text-2xl">
                          {section.heading}
                        </h3>
                        <div className="mt-5 space-y-4 text-[15px] leading-8 sm:text-base">
                          {section.paragraphs.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                          ))}
                        </div>
                      </section>
                    ))}

                    <div className="border-t border-gray-200 pt-7 text-center">
                      <p className="font-bold tracking-wide text-gray-950" lang="en" dir="ltr">
                        GOLD LABEL GLOBAL LIMITED
                      </p>
                      <p className="mt-2 text-sm text-amber-700">{poster.tagline}</p>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
